import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { PaginatedResponse } from './paginated-response.model';
import { PaginatedService } from './paginated-service.model';
import { HttpOptions } from '../http-options.model';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export class PaginatedDataSource<T> implements DataSource<T> {
  // Paginated result
  private paginatedDataSubject: Subject<PaginatedResponse<T>> = new Subject<PaginatedResponse<T>>();
  public readonly paginatedData$: Observable<PaginatedResponse<T>> = this.paginatedDataSubject.asObservable();
  // Loading
  private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  // Total
  private totalSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly total$: Observable<number> = this.totalSubject.asObservable();
  // Items
  private itemsSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  public readonly items$: Observable<T[]> = this.itemsSubject.asObservable();
  // Subject to trigger load requests - switchMap will cancel previous in-flight requests
  private loadTrigger: Subject<HttpOptions | undefined> = new Subject<HttpOptions | undefined>();
  private loadSubscription: Subscription;

  constructor(
    private paginatedService: PaginatedService<T>,
  ) {
    // Set up the switchMap pipeline to automatically cancel previous requests
    this.loadSubscription = this.loadTrigger.pipe(
      tap(() => this.loadingSubject.next(true)),
      switchMap((options: HttpOptions | undefined) =>
        this.paginatedService.getPaginatedList(options).pipe(
          catchError(() => of(new PaginatedResponse<T>())),
        ),
      ),
    ).subscribe((paginatedResponse: PaginatedResponse<T>) => {
      this.loadingSubject.next(false);
      this.paginatedDataSubject.next(paginatedResponse);
      this.totalSubject.next(paginatedResponse.total);
      this.itemsSubject.next(paginatedResponse.items);
    });
  }

  public connect(): Observable<T[]> {
    return this.paginatedDataSubject.asObservable().pipe(
      map((paginatedResponse: PaginatedResponse<T>) => paginatedResponse.items),
    );
  }

  public disconnect(): void {
    this.loadingSubject.complete();
    this.paginatedDataSubject.complete();
    this.loadTrigger.complete();
    this.loadSubscription.unsubscribe();
  }

  public loadData(options?: HttpOptions): void {
    this.loadTrigger.next(options);
  }
}
